import { useEffect, useMemo, useState, useRef } from "react";

import { ensureCsrfToken, getLegacyUrl } from "../../api";
import ProcessorViewShell from "./ProcessorViewShell";

import { openInvoiceData } from "../../utils/invoiceDisplay";
import { openInvoicePdf } from "../../utils/openPdf";

import Swal from "sweetalert2";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}

function TrailModal({ trail, loading, onClose }) {
  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}

export default function ProcessorMandatoryInfoView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [editForm, setEditForm] = useState({
    InvoiceGroupKey: "",
    InvoiceNo: "",
    PoNumber: "",
    GrnNumber: "",
    VendorGst: "",
    VendorCode: "",
    VendorName: "",
  });

  const [vendorVerification, setVendorVerification] = useState({
    status: "", // success | error
    message: "",
  });

  const [menuPosition, setMenuPosition] = useState(null);
  

  const summary = useMemo(
    () => ({
      count: rows.length,
      withReason: rows.filter((row) => row.message).length,
    }),
    [rows],
  );

  const menuRef = useRef(null);
  
  useEffect(() => {
    if (!menuPosition) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuPosition]);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-mandetory-fields-missing/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Unable to load mandatory info invoices. Backend responded with ${response.status}.`,
        );
      }

      if (!payload) {
        throw new Error("Unable to load mandatory info invoices. Backend returned a non-JSON response.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load mandatory info invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.InvoiceId}/`, {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load trail.");
      }
      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  async function handleAction(row, type) {
    setStatus("");
    setError("");
    setActionKey(`${type}-${row.InvoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey };
      } else if (type === "delete") {
        url = "/api/delete-invoice/";
        body = { invoice_group_key: row.InvoiceGroupKey, tab: "mandatoryinfomissing" };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}d successfully.`);
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  const openEditModal = async (groupKey) => {
    try {
      setEditLoading(true);
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-invoice-details/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          InvoiceGroupKey: groupKey,
          Tab: "mandatory_missing",
        }),
      });

      const result = await response.json();

      if (!result.success) {
        alert(result.message || "Unable to fetch invoice details");
        return;
      }

      setEditForm(result.data);

      setVendorVerification({
        status: "",
        message: "",
      });

      setShowEditModal(true);
    } catch (error) {
      console.error(error);
      alert("Error fetching invoice details");
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const verifyVendorGst = async () => {
    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/lookup-vendor-by-gst/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          gst_number: editForm.VendorGst,
        }),
      });

      const result = await response.json();

      if (result.found) {
        setEditForm((prev) => ({
          ...prev,
          VendorCode: result.vendor_code,
          VendorName: result.vendor_name,
        }));

        setVendorVerification({
          status: "success",
          message: "Vendor found",
        });
      } else {
        setEditForm((prev) => ({
          ...prev,
          VendorCode: "",
          VendorName: "",
        }));

        setVendorVerification({
          status: "error",
          message: "No vendor found for entered GST number",
        });
      }
    } catch (err) {
      console.error(err);

      setVendorVerification({
        status: "error",
        message: "Vendor verification failed",
      });
    }
  };

  const submitEditedInvoice = async () => {
    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/submit-mandatoryinfomissing-invoice/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(editForm),
      });

      const result = await response.json();

      if (result.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message || "Invoice submitted successfully",
          confirmButtonText: "OK",
        });

        setShowEditModal(false);
      } else {
        setShowEditModal(false);
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: result.message || "Submission failed",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error(error);
      setShowEditModal(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to submit invoice",
        confirmButtonText: "OK",
      });
    }
  };

  function openLegacyMandatoryPage() {
    window.localStorage.setItem("activeSection", "MandatoryFieldsMissing");
    window.open(getLegacyUrl("/all-invoices/"), "_blank", "noreferrer");
  }

  return (
    <ProcessorViewShell
      title="Mandatory Info Missing"
      metrics={[
        { label: "Queue", value: summary.count },
        { label: "Reasoned Rows", value: summary.withReason },
      ]}
      actions={
        <>
          <button className="ghost-button" type="button" onClick={loadRows} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          
        </>
      }
    >

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      

      <div className="workspace-panel">
        

        {loading ? (
          <p className="config-note">Loading mandatory-info invoices…</p>
        ) : (
          <div className="config-table processor-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Vendor Name</th>
                  <th>Invoice No.</th>
                  <th>Invoice Date</th>
                  <th>Invoice Value</th>
                  <th>Reason</th>
                  <th>Response</th>
                  <th>File</th>
                  <th>Logs</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((row) => (
                    <tr key={row.InvoiceGroupKey}>
                      <td>{row.InvoiceId}</td>
                      <td>{row.VendorName || "-"}</td>
                      <td>{row.InvNo || "-"}</td>
                      <td>{row.InvDate || "-"}</td>
                      <td>{formatAmount(row.TotalAmount)}</td>
                      <td>{row.message || "-"}</td>
                      <td>
                        {row.unique_name ? (
                          <a
                            href={getLegacyUrl(encodeURI(`/api/invoice-display/?response_file=${row.unique_name}.json`))}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Raw Data
                          </a>
                        ) : (
                          "-"
                        )}
                      </td>
                      
                      <td>
                        {row.InvoiceId ? (
                          <button
                            type="button"
                            onClick={() => openInvoicePdf(row.InvoiceId)}
                          >
                            📎
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                          Logs
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="action-menu-button"
                          onClick={(e) => {

                            if (
                              menuPosition?.row?.InvoiceGroupKey === row.InvoiceGroupKey
                            ) {
                              setMenuPosition(null);
                              return;
                            }

                            const rect = e.currentTarget.getBoundingClientRect();

                            setMenuPosition({
                              row,
                              top: rect.bottom + 5,
                              left: rect.left,
                            });
                          }}
                        >
                          Actions ▼
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10">No invoices found with mandatory info missing.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditModal && (
        <div className="nomigo-edit-modal-overlay">
          <div className="nomigo-edit-modal">
            <div className="nomigo-edit-modal-header">
              <h2>Edit Mandatory Info</h2>

              <button
                type="button"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>

            {/* Section 1 */}

            <div className="nomigo-edit-modal-section">
              <h3>Invoice Information</h3>

              <div className="nomigo-edit-form-grid">

                <div className="nomigo-edit-field">
                  <label>Invoice Number</label>
                  <input
                    type="text"
                    name="InvoiceNo"
                    value={editForm.InvoiceNo || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>PO Number</label>
                  <input
                    type="text"
                    name="PoNumber"
                    value={editForm.PoNumber || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>GRN Number</label>
                  <input
                    type="text"
                    name="GrnNumber"
                    value={editForm.GrnNumber || ""}
                    onChange={handleEditChange}
                  />
                </div>

              </div>
            </div>

            {/* Section 2 */}

            <div className="nomigo-edit-modal-section">
              <h3>Vendor Verification</h3>

              <div className="nomigo-edit-form-grid">

                <div className="nomigo-edit-field">
                  <label>Vendor GST</label>
                  <input
                    type="text"
                    name="VendorGst"
                    value={editForm.VendorGst || ""}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="nomigo-edit-verify-wrapper">
                  <button
                    type="button"
                    className="nomigo-edit-verify-btn"
                    onClick={verifyVendorGst}
                  >
                    Verify Vendor
                  </button>
                </div>

                <div className="nomigo-edit-field">
                  <label>Vendor Code</label>
                  <input
                    type="text"
                    value={editForm.VendorCode || ""}
                    readOnly
                  />
                </div>

                <div className="nomigo-edit-field">
                  <label>Vendor Name</label>
                  <input
                    type="text"
                    value={editForm.VendorName || ""}
                    readOnly
                  />
                </div>

              </div>
            </div>

            <div className="nomigo-edit-footer">
              <button
                type="button"
                className="primary-button"
                onClick={submitEditedInvoice}
              >
                Submit Invoice
              </button>
            </div>

          </div>
        </div>
      )}
      {menuPosition && (
        <div
          ref={menuRef}
          className="global-action-menu"
          style={{
            position: "fixed",
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 999999,
          }}
        >
          <button
            type="button"
            disabled={
              actionKey ===
              `approve-${menuPosition.row.InvoiceGroupKey}`
            }
            onClick={() => {
              handleAction(menuPosition.row, "approve");
              setMenuPosition(null);
            }}
          >
            ✓ Approve
          </button>

          <button
            type="button"
            onClick={() => {
              openEditModal(menuPosition.row.InvoiceGroupKey);
              setMenuPosition(null);
            }}
          >
            ✎ Edit
          </button>

          <button
            type="button"
            disabled={
              actionKey ===
              `reject-${menuPosition.row.InvoiceGroupKey}`
            }
            onClick={() => {
              handleAction(menuPosition.row, "reject");
              setMenuPosition(null);
            }}
          >
            ✕ Reject
          </button>

          <button
            type="button"
            className="danger-item"
            disabled={
              actionKey ===
              `delete-${menuPosition.row.InvoiceGroupKey}`
            }
            onClick={() => {
              handleAction(menuPosition.row, "delete");
              setMenuPosition(null);
            }}
          >
            🗑 Delete
          </button>
        </div>
      )}

      <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
    </ProcessorViewShell>
  );
}
